import React, { useState, useEffect } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import VendrLayout from '../VendrLayout';
import { useClientProfile } from '../context/ClientProfileContext';
import { useVendrProducts } from '../context/VendrProductsContext';
import { useAmazonProducts } from '../context/AmazonProductsContext';
import { useAmazonRegistry } from '../context/AmazonBrandRegistryContext';
import './VendrProductsList.css';

const VendrProductsList = () => {
  const location = useLocation();
  const { isSetupComplete, loading: profileLoading } = useClientProfile();
  const { products, loading: productsLoading, error: productsError, deleteProduct } = useVendrProducts();
  const { products: amazonProducts, loading: amazonLoading, error: amazonError, deleteProduct: deleteAmazonProduct } = useAmazonProducts();
  const { isAmazonEnabled, isAmazonSubmitted, loading: amazonRegistryLoading } = useAmazonRegistry();

  const [deletingId, setDeletingId] = React.useState(null);
  const [deletingAmazonId, setDeletingAmazonId] = React.useState(null);

  const getInitialTab = () => {
    const params = new URLSearchParams(location.search);
    if (params.get('tab') === 'amazon' && isAmazonEnabled) return 'amazon';
    return 'products';
  };

  const [activeTab, setActiveTab] = useState('products');

  useEffect(() => {
    if (!amazonRegistryLoading) {
      setActiveTab(getInitialTab());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amazonRegistryLoading]);

  if (profileLoading || amazonRegistryLoading) {
    return (
      <VendrLayout>
        <div className="vendr-products-loading">Loading...</div>
      </VendrLayout>
    );
  }

  if (!isSetupComplete) return <Navigate to="/vendr/setup" replace />;
  if (isAmazonEnabled && !isAmazonSubmitted) return <Navigate to="/vendr/amazon-registry" replace />;

  const handleDeleteProduct = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    setDeletingId(id);
    await deleteProduct(id);
    setDeletingId(null);
  };

  const handleDeleteAmazonProduct = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    setDeletingAmazonId(id);
    await deleteAmazonProduct(id);
    setDeletingAmazonId(null);
  };

  return (
    <VendrLayout>
      <div className="vendr-products-list">
        <div className="vendr-products-page-header">
          <h1>Products</h1>
        </div>

        {isAmazonEnabled && (
          <div className="vendr-products-tabs">
            <button
              type="button"
              className={`vendr-products-tab ${activeTab === 'products' ? 'vendr-products-tab--active' : ''}`}
              onClick={() => setActiveTab('products')}
            >
              Products
            </button>
            <button
              type="button"
              className={`vendr-products-tab vendr-products-tab--amazon ${activeTab === 'amazon' ? 'vendr-products-tab--active vendr-products-tab--amazon-active' : ''}`}
              onClick={() => setActiveTab('amazon')}
            >
              Amazon products
            </button>
          </div>
        )}

        {activeTab === 'products' && (
          <div className="vendr-products-section">
            <div className="vendr-products-list-header">
              <h2>{isAmazonEnabled ? 'Products' : ''}</h2>
              <div className="vendr-products-list-actions">
                <Link to="/vendr/products/new?mode=import" className="vendr-products-secondary-btn">
                  Import via spreadsheet
                </Link>
                <Link to="/vendr/products/new" className="vendr-products-add-btn">
                  Add product
                </Link>
              </div>
            </div>

            {productsError && <div className="vendr-products-error">{productsError}</div>}

            {productsLoading ? (
              <p className="vendr-products-loading">Loading products...</p>
            ) : products.length === 0 ? (
              <div className="vendr-products-empty">
                <p>No products yet.</p>
                <Link to="/vendr/products/new">Add your first product</Link>
              </div>
            ) : (
              <ul className="vendr-products-ul">
                {products.map((p) => (
                  <li key={p.id} className="vendr-products-li">
                    <div className="vendr-products-li-main">
                      <Link to={`/vendr/products/edit/${p.id}`} className="vendr-products-li-title">
                        {p.product_name || p.sku_number || 'Untitled'}
                      </Link>
                      <span className="vendr-products-li-sku">{p.sku_number}</span>
                      <span className={`vendr-products-li-status vendr-products-li-status--${p.status || 'draft'}`}>
                        {(p.status || 'draft').toUpperCase()}
                      </span>
                    </div>
                    <div className="vendr-products-li-actions">
                      <Link to={`/vendr/products/edit/${p.id}`} className="vendr-products-edit-link">
                        Edit
                      </Link>
                      <button
                        type="button"
                        className="vendr-products-delete-btn"
                        onClick={() => handleDeleteProduct(p.id, p.product_name)}
                        disabled={deletingId === p.id}
                      >
                        {deletingId === p.id ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {activeTab === 'amazon' && isAmazonEnabled && (
          <div className="vendr-products-section vendr-products-section--amazon">
            <div className="vendr-products-list-header">
              <h2>Amazon products</h2>
              <div className="vendr-products-list-actions">
                <Link to="/vendr/amazon-products/import" className="vendr-products-secondary-btn vendr-products-secondary-btn--amazon">
                  Import via spreadsheet
                </Link>
                <Link to="/vendr/amazon-products/new" className="vendr-products-add-btn vendr-products-add-btn--amazon">
                  Add Amazon product
                </Link>
              </div>
            </div>

            <div className="vendr-products-import-note">
              <strong>Note:</strong> Products imported via spreadsheet may be missing required fields, images, and files. Edit each product to complete them before submitting.
            </div>

            {amazonError && <div className="vendr-products-error">{amazonError}</div>}

            {amazonLoading ? (
              <p className="vendr-products-loading">Loading Amazon products...</p>
            ) : amazonProducts.length === 0 ? (
              <div className="vendr-products-empty">
                <p>No Amazon products yet.</p>
                <Link to="/vendr/amazon-products/new">Add your first Amazon product</Link>
              </div>
            ) : (
              <ul className="vendr-products-ul">
                {amazonProducts.map((p) => (
                  <li key={p.id} className="vendr-products-li vendr-products-li--amazon">
                    <div className="vendr-products-li-main">
                      <Link to={`/vendr/amazon-products/edit/${p.id}`} className="vendr-products-li-title">
                        {p.product_name || p.sku || 'Untitled'}
                      </Link>
                      <span className="vendr-products-li-sku">{p.sku}</span>
                      {p.brand_name && (
                        <span className="vendr-products-li-brand">{p.brand_name}</span>
                      )}
                      <span className={`vendr-products-li-status vendr-products-li-status--${p.status || 'draft'}`}>
                        {(p.status || 'draft').toUpperCase()}
                      </span>
                    </div>
                    <div className="vendr-products-li-actions">
                      <Link to={`/vendr/amazon-products/edit/${p.id}`} className="vendr-products-edit-link">
                        Edit
                      </Link>
                      <button
                        type="button"
                        className="vendr-products-delete-btn"
                        onClick={() => handleDeleteAmazonProduct(p.id, p.product_name)}
                        disabled={deletingAmazonId === p.id}
                      >
                        {deletingAmazonId === p.id ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </VendrLayout>
  );
};

export default VendrProductsList;
